import React, { useEffect, useState, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import { useSpring, animated } from 'react-spring';
import { useMutation } from 'urql';
import useInput from './input-hook';

import './styles.css';

const createMessage = `
  mutation createMessage($clientEmail: String!, $subject: String!, $message: String!) {
    createMessage(clientEmail: $clientEmail, subject: $subject, message: $message ) {
      sent
      msg
    }
  }
`;

const propTypes = {
  speedValues: PropTypes.oneOfType([PropTypes.object]),
  scrollPos: PropTypes.number,
  sendEmail: PropTypes.bool.isRequired,
  changeStateEmail: PropTypes.func.isRequired,
  tryToSend: PropTypes.func.isRequired,
  resumeAnim: PropTypes.func.isRequired,
};

const defaultProps = {
  speedValues: {},
  scrollPos: 0,
};

const ContactForm = ({
  speedValues,
  scrollPos,
  sendEmail,
  changeStateEmail,
  tryToSend,
  resumeAnim,
}) => {
  const [startSendEmail, setStartSendEmail] = useState(false);
  const [hideInputFields, setHideInputFields] = useState(false);
  const [res, executeMutation] = useMutation(createMessage);
  const lastProps = useRef(sendEmail);

  const {
    value: userEmail,
    bind: bindUserEmail,
  } = useInput('');
  const {
    value: userSubject,
    bind: bindUserSubject,
  } = useInput('');
  const {
    value: userMessage,
    bind: bindUserMessage,
  } = useInput('');

  const { speedFast } = useSpring({
    from: { speedFast: [scrollPos, 0, 0] },
    to: { speedFast: [speedValues.fast, 0, 0] },
  });

  useEffect(() => {
    const validationForm = () => {
      const regex = /^[^\s@]+@[^\s@]+\./;

      if (!regex.test(userEmail)) {
        setStartSendEmail(false);
        tryToSend(false);
        changeStateEmail('invalidEmail');

        return false;
      }

      if (
        !userEmail.replace(/\s/g, '').length
        || !userSubject.replace(/\s/g, '').length
        || !userMessage.replace(/\s/g, '').length) {
        setStartSendEmail(false);
        tryToSend(false);
        changeStateEmail('emptyFields');

        return false;
      }

      return true;
    };

    const handleSubmit = async () => {
      setStartSendEmail(true);
      const isValid = validationForm();

      if (isValid) {
        changeStateEmail('waiting');

        const data = {
          clientEmail: userEmail,
          subject: userSubject,
          message: userMessage,
        };

        await executeMutation(data).then(resp => {
          tryToSend(false);
          // eslint-disable-next-line no-prototype-builtins
          if (resp.data !== undefined && resp.data.hasOwnProperty('createMessage')) {
            if (resp.data.createMessage.sent) {
              changeStateEmail('success');
              setHideInputFields(true);
              resumeAnim();
            } else {
              changeStateEmail('errorSendEmail');
            }
          }
        });

        if (res.error) {
          return false;
        }
      }

      return false;
    };

    if (lastProps.current !== sendEmail && !startSendEmail) {
      handleSubmit();
    }
  }, [
    changeStateEmail,
    sendEmail,
    startSendEmail,
    userEmail,
    userMessage,
    userSubject,
    tryToSend,
    resumeAnim,
  ]);

  return (
    <div className={`${isMobile ? 'mobile' : ''} contact-form ${hideInputFields ? 'hide-fields' : ''}`}>
      <animated.div
        className={`${isMobile ? 'mobile' : ''} layer-1`}
        style={{
          transform: speedFast.to(
            (x, y, z) => `translate3d(${x}px, ${y}px, ${z}px)`,
          ),
        }}
      >
        <input
          className={`${isMobile ? 'mobile' : ''} inputs input-user-email`}
          type="text"
          {...bindUserEmail}
        />
        <input
          className={`${isMobile ? 'mobile' : ''} inputs input-user-subject`}
          type="text"
          {...bindUserSubject}
        />
        <textarea
          className={`${isMobile ? 'mobile' : ''} inputs input-user-message`}
          type="text"
          {...bindUserMessage}
        />
      </animated.div>
    </div>
  );
};

export default ContactForm;

ContactForm.propTypes = propTypes;
ContactForm.defaultProps = defaultProps;
